// src/config/http.js
// Attaches the JWT to every request that targets the Aquabrim API.
//
// The backend guards its routes with authMiddleware, which rejects anything
// without an `Authorization: Bearer <token>` header (401 "No token provided"),
// and more routes keep being moved behind it. Registering the token once here
// covers every axios call in the app — including the many that pass no config
// object at all — instead of relying on each call site to remember.
//
// Imported for its side effect from src/index.js, before anything renders.
import axios from 'axios';
import { API_BASE_URL } from './config';

// Case-insensitive read that works for both AxiosHeaders (axios 1.x) and the
// plain objects call sites hand-build.
const hasAuthHeader = (headers) => {
  if (!headers) return false;
  if (typeof headers.get === 'function') return Boolean(headers.get('Authorization'));
  return Object.keys(headers).some((k) => k.toLowerCase() === 'authorization');
};

const setAuthHeader = (config, value) => {
  if (config.headers && typeof config.headers.set === 'function') {
    config.headers.set('Authorization', value);
  } else {
    config.headers = { ...(config.headers || {}), Authorization: value };
  }
};

axios.interceptors.request.use((config) => {
  const url = config.url || '';

  // Only our own API — never hand the token to a third-party host.
  const isApiCall = url.startsWith(API_BASE_URL) || url.startsWith('/api/');
  if (!isApiCall) return config;

  // A call site that set the header deliberately wins (the redux thunks read
  // the token out of the store rather than localStorage).
  if (hasAuthHeader(config.headers)) return config;

  const token = localStorage.getItem('token');
  if (token) setAuthHeader(config, `Bearer ${token}`);

  return config;
});

export default axios;
